import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { readStored, writeStored } from '../storage';
import { translate } from './translate';
import { resolveLanguage } from './resolve';
import { DEFAULT_LANGUAGE, SHIPPED_LANGUAGES, dirFor, isShippedLanguageCode } from './languages';

/**
 * Which language the site is being read in.
 *
 * Kept apart from ThemeContext and SiteContext for the same reason those are
 * kept apart from each other: this one owns language, they own appearance and
 * filtering, and none of them needs to know about the others. The choice
 * round-trips through the URL the same way, so `?lang=fr` is a link you can
 * send.
 *
 * English is the base: its content lives in the data files and its UI strings
 * are compiled in, so an English reader downloads no locale chunk at all.
 */

const I18nContext = createContext(null);

export const STORAGE_KEY = 'ht-lang';

export const useI18n = () => {
  const value = useContext(I18nContext);
  if (!value) throw new Error('useI18n must be used inside <I18nProvider>');
  return value;
};

export const useT = () => useI18n().t;

/**
 * One locale file. The template literal is what lets webpack split these into
 * a chunk each, so a reader downloads only the language they asked for.
 */
const loadLocale = (code) =>
  import(/* webpackChunkName: "locale-[request]" */ `./locales/${code}.json`)
    .then((module) => module.default || module);

export function I18nProvider({ children }) {
  const [lang, setLangState] = useState(() =>
    resolveLanguage({
      search: typeof window === 'undefined' ? '' : window.location.search,
      stored: readStored(STORAGE_KEY),
      languages: typeof navigator === 'undefined' ? [] : navigator.languages || [],
    })
  );

  // null means "English", which needs no overlay. Kept through a language
  // change until the next file arrives, so switching never flashes English.
  const [dict, setDict] = useState(null);

  useEffect(() => {
    let cancelled = false;

    if (lang === DEFAULT_LANGUAGE) {
      setDict(null);
      return undefined;
    }

    loadLocale(lang)
      .then((loaded) => {
        if (!cancelled) setDict(loaded);
      })
      .catch(() => {
        // A locale that will not load is a deployment problem, not a reason to
        // show the reader a blank page. English is always available.
        if (!cancelled) setDict(null);
        // eslint-disable-next-line no-console
        console.warn(`[i18n] could not load "${lang}", staying on English`);
      });

    return () => { cancelled = true; };
  }, [lang]);

  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute('lang', lang);
    root.setAttribute('dir', dirFor(lang));

    writeStored(STORAGE_KEY, lang);

    // replaceState, not pushState: choosing a language is not navigation, and
    // stacking history entries would make the back button feel broken.
    const params = new URLSearchParams(window.location.search);
    if (lang === DEFAULT_LANGUAGE) params.delete('lang');
    else params.set('lang', lang);

    const search = params.toString();
    window.history.replaceState(
      null,
      '',
      `${window.location.pathname}${search ? `?${search}` : ''}${window.location.hash}`
    );
  }, [lang]);

  // Title and description follow the language too, so a shared link previews
  // in the language it opens in. og:description and twitter:description are
  // updated alongside the plain description tag — those are what Facebook,
  // LinkedIn, Slack and X actually read when unfurling a shared link, and a
  // stale English blurb under a translated title/description would be a
  // half-translated preview.
  useEffect(() => {
    document.title = translate(dict?.ui, 'meta.title');

    const description = translate(dict?.ui, 'meta.description');
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute('content', description);

    const socialDescription = translate(dict?.ui, 'meta.socialDescription');
    const ogDescription = document.querySelector('meta[property="og:description"]');
    if (ogDescription) ogDescription.setAttribute('content', socialDescription);
    const twitterDescription = document.querySelector('meta[name="twitter:description"]');
    if (twitterDescription) twitterDescription.setAttribute('content', socialDescription);
  }, [dict]);

  const setLanguage = useCallback((code) => {
    if (isShippedLanguageCode(code)) setLangState(code);
  }, []);

  const t = useCallback((key, vars) => translate(dict?.ui, key, vars, lang), [dict, lang]);

  // languages: SHIPPED_LANGUAGES, not the full registry — every consumer
  // (header switcher, ⌘K palette, footer) renders this list directly, and an
  // unshipped entry here would make it choosable with no locale file behind
  // it: resolveLanguage() would then refuse it and silently fall back to
  // English while lang="xx" sat on the document.
  const value = useMemo(
    () => ({ lang, setLanguage, languages: SHIPPED_LANGUAGES, dict, t, dir: dirFor(lang) }),
    [lang, setLanguage, dict, t]
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}
